// Ambulation Field — a Perlin-noise flow-field of wandering ambulators (walkers)
// leaving fading rust/sepia trails. Periodically the walkers drift toward a
// center point and a REAL eye photo (theeye.png) surfaces out of the swarm —
// doctored to suit this program's uncanny character: cold-tinted, posterized,
// RGB channel-split, eroded by the particles, smeared with grain + scanlines,
// jittering, then dissolving back into the field. On-brand to the name
// "Ambulation" and the recurring menacing-eye motif.
// React-p5 class-wrapper convention. ~450x350. Default RGB color mode.

import React, { Component } from 'react';
import p5 from 'p5';
import { pauseWhenHidden, prefersReducedMotion } from '../_shared/sketchFrame';
// CRA/webpack resolves this import to a URL string we can feed to loadImage.
// A file at src/pages/Components/AmbulationField/ reaches src/images/ via ../../../images/.
import theEyeUrl from '../../../images/theeye.png';
import './ambulationfield.css';

const W = 450;
const H = 350;

class AmbulationField extends Component {
    constructor(props) {
        super(props);
        this.myRef = React.createRef();
        this._cleanupFrame = null; // holds the pauseWhenHidden cleanup fn
        // persistent on the instance; transient state lives inside Sketch
        this.walkers = [];
        this.t = 0;
    }

    Sketch = (p) => {
        // Fewer walkers: the translucent fade-fill already implies density, so
        // halving the swarm roughly halves per-frame point cost with no loss of
        // the rust/sepia flow-field look or the coalescing eye.
        const N = 80;
        let zoff = 0;

        // The eye, as drawn into the canvas: a wide ellipse footprint centered
        // on the field. The doctored photo is fitted into this box.
        const cx = 225;
        const cy = 175;
        const eyeW = 220;
        const eyeH = 150;

        // Eye assets, populated once the image loads. Until then the sketch
        // runs identically minus the eye, so a slow/failed load never breaks it.
        let eyeReady = false;
        let eyeDoctored = null; // p5.Image: posterized, cold-tinted, fitted to eyeW x eyeH
        let eyeBright = null;   // array of {x,y} canvas points sampled from the iris/veins

        // Doctor the loaded photo into something uncanny: scale-to-fit, drop to a
        // cold posterized palette, and bias toward the blue end so the warm
        // rust field reads it as an intruder rather than a clean stock eyeball.
        const doctorEye = (src) => {
            const img = p.createImage(eyeW, eyeH);
            // Fit the (near-square) source into the wider eye box.
            const fitted = src.get();
            fitted.resize(eyeW, eyeH);
            fitted.loadPixels();
            img.loadPixels();

            const bright = [];
            for (let y = 0; y < eyeH; y++) {
                for (let x = 0; x < eyeW; x++) {
                    const i = 4 * (y * eyeW + x);
                    let r = fitted.pixels[i];
                    let g = fitted.pixels[i + 1];
                    let b = fitted.pixels[i + 2];

                    // luminance → posterize into a few cold bands
                    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
                    const band = Math.floor((lum / 256) * 5) / 4; // 0..1 in 5 steps

                    // cold, slightly sickly remap: kill warmth, push blue/teal
                    r = band * 70;
                    g = band * 120 + 10;
                    b = band * 165 + 25;

                    img.pixels[i] = r;
                    img.pixels[i + 1] = g;
                    img.pixels[i + 2] = b;
                    img.pixels[i + 3] = 255;

                    // remember the brighter structure (iris striations, veins,
                    // sclera highlights) so walkers can trace it on their own.
                    if (lum > 150 && (x + y) % 3 === 0) {
                        bright.push({
                            x: cx - eyeW / 2 + x,
                            y: cy - eyeH / 2 + y,
                        });
                    }
                }
            }
            img.updatePixels();
            eyeDoctored = img;
            eyeBright = bright;
            eyeReady = true;
        };

        p.setup = () => {
            p.createCanvas(W, H);
            // Ambient ambient sketch — 30fps is plenty for the drifting trails
            // and halves ongoing GPU/CPU cost versus the default 60.
            p.frameRate(30);
            p.background(12, 8, 6);

            // Load the real eye, then doctor it once. Instance-mode p5 in this
            // React wrapper has no global preload(), so we load in setup() and
            // simply guard every use behind eyeReady.
            p.loadImage(
                theEyeUrl,
                (loaded) => doctorEye(loaded),
                () => { eyeReady = false; } // load failed: run as the pure particle field
            );

            // Respect the user's reduced-motion preference: render one frame, no loop.
            if (prefersReducedMotion()) p.noLoop();
            this.walkers = [];
            for (let i = 0; i < N; i++) {
                this.walkers.push({
                    pos: p.createVector(p.random(W), p.random(H)),
                    vel: p.createVector(0, 0),
                });
            }
        };

        p.draw = () => {
            // translucent trail fade — leaves fading rust/sepia ghosts
            p.noStroke();
            p.fill(12, 8, 6, 18);
            p.rect(0, 0, W, H);

            // every ~600 frames the field gazes for the final 120 frames
            const phase = this.t % 600;
            const gazing = phase > 480;
            // 0 → 1 → 0 envelope across the gaze so the eye surfaces and dissolves
            // rather than snapping on/off.
            let gaze = 0;
            if (gazing) {
                const g = (phase - 480) / 120; // 0..1 across the gaze window
                gaze = p.sin(g * p.PI); // ease in, peak, ease back out
            }

            for (let i = 0; i < this.walkers.length; i++) {
                const w = this.walkers[i];

                if (gazing && eyeReady && eyeBright.length && i % 2 === 0) {
                    // half the swarm is drawn toward bright eye structure, so the
                    // particles themselves redraw the iris striations and veins —
                    // the eye is reconstituted out of walkers, not just stamped.
                    const target = eyeBright[(i * 7 + this.t) % eyeBright.length];
                    const a = p.atan2(target.y - w.pos.y, target.x - w.pos.x);
                    w.vel.x += p.cos(a) * 0.3;
                    w.vel.y += p.sin(a) * 0.3;
                } else if (gazing) {
                    // the rest steer toward the watching center
                    const a = p.atan2(cy - w.pos.y, cx - w.pos.x);
                    w.vel.x += p.cos(a) * 0.25;
                    w.vel.y += p.sin(a) * 0.25;
                } else {
                    // wander the Perlin flow-field
                    const angle =
                        p.noise(w.pos.x * 0.004, w.pos.y * 0.004, zoff) *
                        p.TWO_PI *
                        2;
                    w.vel.x += p.cos(angle) * 0.15;
                    w.vel.y += p.sin(angle) * 0.15;
                }

                w.vel.limit(2);
                w.pos.add(w.vel);

                // wrap edges
                if (w.pos.x < 0) w.pos.x += W;
                if (w.pos.x > W) w.pos.x -= W;
                if (w.pos.y < 0) w.pos.y += H;
                if (w.pos.y > H) w.pos.y -= H;

                // warm rust/sepia trails — faster walkers run hotter
                const r = p.map(w.vel.mag(), 0, 2, 120, 200);
                p.stroke(r, 70, 30, 90);
                p.strokeWeight(2);
                p.point(w.pos.x, w.pos.y);
            }

            // When gazing, the doctored eye photo rises out of the swarm. It is
            // never shown clean: RGB channel-split, eroded by the particle field,
            // jittering, and dusted with grain + scanlines so it reads as a
            // half-formed, disturbed eye watching back.
            if (gazing && eyeReady && eyeDoctored) {
                const x0 = cx - eyeW / 2;
                const y0 = cy - eyeH / 2;
                const a = gaze * 165; // overall opacity follows the gaze envelope
                const split = 2 + gaze * 5; // channel offset widens as it surfaces
                const jx = p.random(-1.5, 1.5);
                const jy = p.random(-1.5, 1.5);

                p.push();
                // ADD blend makes the cold eye glow up out of the dark field and
                // the channel-split fringes read as chromatic aberration.
                p.blendMode(p.ADD);
                p.noStroke();
                // red ghost (offset one way)
                p.tint(255, 40, 40, a * 0.7);
                p.image(eyeDoctored, x0 - split + jx, y0 + jy);
                // cyan ghost (offset the other way) — the split that makes it queasy
                p.tint(40, 200, 255, a * 0.85);
                p.image(eyeDoctored, x0 + split + jx, y0 - jy);
                // pale core, mostly aligned, holding the form together
                p.tint(200, 220, 255, a);
                p.image(eyeDoctored, x0 + jx * 0.4, y0 + jy * 0.4);
                p.pop();

                // erode the photo with the dark field: punch ragged holes of the
                // background back through it so it never resolves into a whole eye.
                p.noStroke();
                p.fill(12, 8, 6, 110 * (1 - gaze * 0.4));
                for (let k = 0; k < 26; k++) {
                    const ex = x0 + p.random(eyeW);
                    const ey = y0 + p.random(eyeH);
                    p.ellipse(ex, ey, p.random(3, 11), p.random(3, 11));
                }

                // scanlines across the eye box — CRT/VHS interference
                p.stroke(0, 0, 0, 70 * gaze);
                p.strokeWeight(1);
                for (let sy = y0; sy < y0 + eyeH; sy += 3) {
                    p.line(x0, sy, x0 + eyeW, sy);
                }

                // grain dither over the iris area
                p.noStroke();
                for (let k = 0; k < 40; k++) {
                    const gxp = x0 + p.random(eyeW);
                    const gyp = y0 + p.random(eyeH);
                    p.fill(p.random(80, 220), p.random(120, 220), p.random(160, 255), 60 * gaze);
                    p.rect(gxp, gyp, 1, 1);
                }

                // the pupil still jitters — a hard black hole watching back
                p.fill(0, 230 * gaze);
                p.ellipse(cx + p.random(-4, 4), cy + p.random(-4, 4), 30, 30);
            }

            zoff += 0.01;
            this.t++;
        };

        // optional ambient nudge: drag a tiny eddy of walkers toward the cursor
        p.mouseMoved = () => {
            if (
                p.mouseX < 0 ||
                p.mouseX > W ||
                p.mouseY < 0 ||
                p.mouseY > H
            ) {
                return;
            }
            for (let i = 0; i < 6; i++) {
                const w = this.walkers[Math.floor(p.random(this.walkers.length))];
                if (!w) continue;
                const a = p.atan2(p.mouseY - w.pos.y, p.mouseX - w.pos.x);
                w.vel.x += p.cos(a) * 0.6;
                w.vel.y += p.sin(a) * 0.6;
            }
        };
    };

    componentDidMount() {
        this.myp5 = new p5(this.Sketch, this.myRef.current);
        // Pause the draw loop when scrolled off-screen or the tab is hidden;
        // resume (and repaint) when it returns. Drops this ambient sketch's
        // steady-state cost to zero whenever it isn't actually being watched.
        this._cleanupFrame = pauseWhenHidden(this.myp5, this.myRef.current);
    }

    componentWillUnmount() {
        // Detach the IntersectionObserver + visibilitychange listener first...
        if (this._cleanupFrame) {
            this._cleanupFrame();
            this._cleanupFrame = null;
        }
        // ...then tear down the p5 instance (stops the draw loop, frees the canvas).
        if (this.myp5) {
            this.myp5.remove();
            this.myp5 = null;
        }
    }

    render() {
        return <div ref={this.myRef} className="p5-wrap ambulation-field-wrap"></div>;
    }
}

export default AmbulationField;
